// ── A buyer's Thai on the POA (report b4f708fa) ──────────────────────────────────────────────────
// The reporter: the POA's Thai donor paragraph "should be all in the Thai language, including names and addresses".
// These are the POA card's Thai boxes for one buyer (`poa.grantors[uid]`): the name, the parents' names and the address in
// its parts, each with the English on the buyer's card it stands for. That English is the box's placeholder, and what the
// POA prints while the box is empty.
// While the POA card is open the staff screen asks once for the boxes still empty (FormBody, POST /:id/poa/translate)
// and fills only those still empty when the answers come back, so typed Thai is never overwritten. `thAi` on the buyer's
// answers keeps the English the AI was given for each box it filled, and typing in a box takes that off:
//   · a record that still matches: the value is the AI's. The card asks for it to be checked, and the POA prints it on
//     the yellow highlight (server/src/services/poaTemplates.ts);
//   · a record that no longer matches (a name typed further, a block cleared): the value translates nothing now, so the
//     box counts as blank. It shows empty, the POA prints the English, and the screen asks again.
// Mirror of the same rules in server/src/services/propertyPoa.ts — keep the two in step. A file of its own, free of
// React, so that server/src/scripts/testLeaseAgreements.ts can run these rules.
export type PoaThaiBox = { key: string; en: string };
export type PoaThaiFill = PoaThaiBox & { uid: string; th: string };

const TH_ADDRESS = 'addrTh.';
const tidy = (v: any): string => String(v ?? '').trim();
const spaced = (...v: any[]): string => v.map(tidy).filter(Boolean).join(' ');
const partOf = (key: string): string => (key.indexOf(TH_ADDRESS) === 0 ? key.slice(TH_ADDRESS.length) : '');

/** The address parts with a Thai box, each with its label. The Post Code has none: its digits print as they are. */
export const POA_TH_ADDRESS: [string, string][] = [
  [`${TH_ADDRESS}line1`, 'pf_poa_addr_line1_th'], [`${TH_ADDRESS}line2`, 'pf_poa_addr_line2_th'],
  [`${TH_ADDRESS}city`, 'pf_poa_addr_city_th'], [`${TH_ADDRESS}country`, 'pf_poa_addr_country_th'],
];
export const poaThaiBoxes = (b: any): PoaThaiBox[] => {
  const raw = b?.address;
  const a = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : { line1: raw };
  return [
    { key: 'firstTh', en: spaced(b?.first, b?.middle) },
    { key: 'lastTh', en: tidy(b?.last) },
    { key: 'fatherTh', en: spaced(b?.fatherFirst, b?.fatherLast) },
    { key: 'motherTh', en: spaced(b?.motherFirst, b?.motherLast) },
    ...POA_TH_ADDRESS.map(([key]) => ({ key, en: tidy(a[partOf(key)]) })),
  ];
};

const recordOf = (g: any, key: string): any => (g?.thAi || {})[key];
const stale = (g: any, box: PoaThaiBox): boolean => recordOf(g, box.key) != null && tidy(recordOf(g, box.key)) !== box.en;
/** What a Thai box counts as holding, as typed: its own spaces stay while somebody is typing in it. */
export const poaThaiNow = (g: any, box: PoaThaiBox): string => {
  if (stale(g, box)) return '';
  const part = partOf(box.key);
  return String((part ? (g?.addrTh || {})[part] : g?.[box.key]) ?? '');
};
/** The box holds the AI's Thai: to be checked. */
export const poaThaiIsAi = (g: any, box: PoaThaiBox): boolean => recordOf(g, box.key) != null && !!tidy(poaThaiNow(g, box));

/** English with a letter of another script to put into Thai. A house number alone, or Thai in the English box, prints as it is. */
const needsThai = (en: string): boolean => /[^\P{L}ก-๿]/u.test(en);
/** The boxes that are for the AI to fill, each as `uid\nkey\nenglish`: the same box with the same English is asked about once. */
export const poaThaiWanted = (d: any): string[] => (Array.isArray(d?.buyers) ? d.buyers : []).flatMap((b: any) => {
  if (!b || !b.uid) return [];
  const g = ((d.poa && d.poa.grantors) || {})[b.uid];
  return poaThaiBoxes(b).filter(x => needsThai(x.en) && !tidy(poaThaiNow(g, x))).map(x => `${b.uid}\n${x.key}\n${x.en}`);
});

/** A buyer's answers with one Thai box changed. `from` = the English the AI was given; none = somebody typed it. */
const withThai = (g: any, key: string, v: string, from?: string): any => {
  const thAi = { ...(g?.thAi || {}) };
  if (from === undefined) delete thAi[key]; else thAi[key] = from;
  const part = partOf(key);
  return { ...(g || {}), ...(part ? { addrTh: { ...(g?.addrTh || {}), [part]: v } } : { [key]: v }), thAi };
};
/** The form with one buyer's Thai box typed in: from now on it is theirs, never the AI's. */
export const poaThaiTyped = (d: any, uid: string, key: string, v: string): any => {
  const gs = (d.poa && d.poa.grantors) || {};
  return { ...d, poa: { ...(d.poa || {}), grantors: { ...gs, [uid]: withThai(gs[uid], key, v) } } };
};
/** The form with the AI's answers in: each only into a box that still counts as blank, while its buyer's English is
 *  what the AI was given. The form comes back as it was when none goes in. */
export const poaThaiFilled = (d: any, fills: PoaThaiFill[]): any => {
  const gs = { ...((d.poa && d.poa.grantors) || {}) };
  let n = 0;
  for (const f of fills) {
    const box = poaThaiBoxes((d.buyers || []).find((b: any) => b && b.uid === f.uid)).find(x => x.key === f.key && x.en === f.en);
    if (!box || !tidy(f.th) || tidy(poaThaiNow(gs[f.uid], box))) continue;
    gs[f.uid] = withThai(gs[f.uid], f.key, tidy(f.th), f.en);
    n++;
  }
  return n ? { ...d, poa: { ...(d.poa || {}), grantors: gs } } : d;
};
